import { useMemo } from 'react';
import {
  WORLD_EXPLORER_BY_ID,
  WORLD_EXPLORER_IDS,
} from '../components/worldExplorer/worldExplorerData';
import { getPathLessons as getPathLessonIds, getSkillForLesson, isSkillUnlocked } from './skillGraph';
import { getGuidedLesson } from './guidedLessonRegistry';
import {
  makeLessonKey,
  useProgressSnapshot,
  useReviewQueue,
} from './progressStore';
import { useAgeBand, normalizeAgeRange } from './ageBand';

/**
 * Pure recommendation selectors. No side effects, all derived from
 * progressStore + worldExplorerData + the JSON skill graph.
 *
 * Three calm signals — designed without streaks, XP, or daily quotas:
 *  1. resume:        the lesson the learner most recently touched.
 *  2. todaysPick:    a single suggested lesson (review > age-band match > fresh).
 *  3. exploreNext:   a never-touched world to broaden interests.
 */

/**
 * @typedef {{
 *   worldId: string,
 *   worldTitle: string,
 *   worldIcon: string,
 *   pathId: string,
 *   pathTitle: string,
 *   lessonId: string,
 *   lessonTitle: string,
 *   reason: 'resume' | 'review' | 'continue-path' | 'fresh-age-match' | 'fresh-any' | 'explore',
 *   ageBand: '5-8' | '9-12' | null,
 *   estimatedMinutes: number,
 *   locked?: boolean
 * }} Recommendation
 */

function findPathForLesson(worldId, lessonId) {
  const world = WORLD_EXPLORER_BY_ID[worldId];
  if (!world) return null;
  return (
    world.learningPaths.find((p) => getPathLessonIds(worldId, p.id).includes(lessonId)) || null
  );
}

function buildRec(worldId, pathId, lessonId, reason) {
  const world = WORLD_EXPLORER_BY_ID[worldId];
  const lesson = getGuidedLesson(worldId, lessonId);
  const path = world?.learningPaths.find((p) => p.id === pathId);
  if (!world || !lesson || !path) return null;
  return {
    worldId,
    worldTitle: world.title,
    worldIcon: world.icon,
    pathId,
    pathTitle: path.title,
    lessonId,
    lessonTitle: lesson.title,
    reason,
    ageBand: normalizeAgeRange(lesson.ageRange),
    estimatedMinutes: lesson.estimatedMinutes,
  };
}

/**
 * Iterate all (world, path, lesson) tuples in a stable order.
 * @returns {Array<{ worldId: string, pathId: string, lessonId: string }>}
 */
function allLessonTuples() {
  const out = [];
  for (const worldId of WORLD_EXPLORER_IDS) {
    const world = WORLD_EXPLORER_BY_ID[worldId];
    if (!world?.learningPaths) continue;
    for (const path of world.learningPaths) {
      for (const lessonId of getPathLessonIds(worldId, path.id)) {
        out.push({ worldId, pathId: path.id, lessonId });
      }
    }
  }
  return out;
}

/**
 * Resume target = the most recently completed lesson's *next* incomplete sibling
 * in the same path. Falls back to "first started but not completed" lesson.
 *
 * @param {ReturnType<typeof useProgressSnapshot>} progress
 * @returns {Recommendation | null}
 */
export function getResumeTarget(progress) {
  if (!progress) return null;
  const completedSet = new Set(progress.completedLessons || []);
  const startedSet = new Set(progress.startedLessons || []);

  const pickFirstIncompleteInPath = (worldId, pathId) => {
    const ids = getPathLessonIds(worldId, pathId);
    return ids.find((lid) => !completedSet.has(makeLessonKey(worldId, lid))) || null;
  };

  // 1. Prefer continuing the most recently visited world if it has an incomplete lesson.
  if (progress.lastVisitedWorld) {
    const wid = progress.lastVisitedWorld;
    const world = WORLD_EXPLORER_BY_ID[wid];
    if (world) {
      for (const path of world.learningPaths) {
        const next = pickFirstIncompleteInPath(wid, path.id);
        if (next && (startedSet.has(makeLessonKey(wid, next)) || completedSet.size > 0)) {
          return buildRec(wid, path.id, next, 'resume');
        }
      }
    }
  }

  // 2. Any started-but-incomplete lesson, anywhere.
  for (const lessonKey of startedSet) {
    if (completedSet.has(lessonKey)) continue;
    const [worldId, lessonId] = lessonKey.split(':');
    if (!worldId || !lessonId) continue;
    const path = findPathForLesson(worldId, lessonId);
    if (path) return buildRec(worldId, path.id, lessonId, 'resume');
  }

  // 3. Continue any path the learner has touched (next incomplete in that path).
  if (progress.lastVisitedWorld) {
    const wid = progress.lastVisitedWorld;
    const world = WORLD_EXPLORER_BY_ID[wid];
    if (world) {
      for (const path of world.learningPaths) {
        const next = pickFirstIncompleteInPath(wid, path.id);
        if (next) return buildRec(wid, path.id, next, 'continue-path');
      }
    }
  }

  return null;
}

/**
 * Today's pick: prefer a calm review when something is due, otherwise the next
 * unstarted lesson that matches the active age band.
 *
 * @param {ReturnType<typeof useProgressSnapshot>} progress
 * @param {ReturnType<typeof useReviewQueue>} reviewQueue
 * @param {'5-8' | '9-12' | null} activeAgeBand
 * @returns {Recommendation | null}
 */
export function getTodaysPick(progress, reviewQueue, activeAgeBand) {
  if (!progress) return null;
  const completedSet = new Set(progress.completedLessons || []);

  // 1. Review surfaces first. Use the oldest due item.
  if (reviewQueue && reviewQueue.length > 0) {
    const head = reviewQueue[0];
    const [worldId, lessonId] = head.lessonKey.split(':');
    const path = findPathForLesson(worldId, lessonId);
    if (path) {
      const r = buildRec(worldId, path.id, lessonId, 'review');
      if (r) return r;
    }
  }

  const tuples = allLessonTuples();

  const isUnlocked = (worldId, lessonId) => {
    const skill = getSkillForLesson(worldId, lessonId);
    if (!skill) return true;
    return isSkillUnlocked(worldId, skill.id, completedSet);
  };

  const incompleteAndUnlocked = tuples.filter(
    ({ worldId, lessonId }) =>
      !completedSet.has(makeLessonKey(worldId, lessonId)) && isUnlocked(worldId, lessonId),
  );

  // 2. First unlocked + incomplete lesson that matches the active age band.
  if (activeAgeBand) {
    const match = incompleteAndUnlocked.find(({ worldId, lessonId }) => {
      const lesson = getGuidedLesson(worldId, lessonId);
      return normalizeAgeRange(lesson?.ageRange) === activeAgeBand;
    });
    if (match) {
      return buildRec(match.worldId, match.pathId, match.lessonId, 'fresh-age-match');
    }
  }

  // 3. Fallback to any unlocked + incomplete lesson.
  const any = incompleteAndUnlocked[0];
  if (any) return buildRec(any.worldId, any.pathId, any.lessonId, 'fresh-any');

  return null;
}

/**
 * Suggest a world the learner has never opened, to gently broaden interests.
 * Returns just the world record (not a lesson).
 *
 * @param {ReturnType<typeof useProgressSnapshot>} progress
 * @returns {{ worldId: string, worldTitle: string, worldIcon: string, reason: 'explore' } | null}
 */
export function getExploreNextWorld(progress) {
  if (!progress) return null;
  const startedSet = new Set(progress.startedLessons || []);
  const visitedWorlds = new Set();
  startedSet.forEach((k) => {
    const [w] = k.split(':');
    if (w) visitedWorlds.add(w);
  });
  if (progress.lastVisitedWorld) visitedWorlds.add(progress.lastVisitedWorld);

  for (const wid of WORLD_EXPLORER_IDS) {
    if (visitedWorlds.has(wid)) continue;
    const world = WORLD_EXPLORER_BY_ID[wid];
    if (!world) continue;
    return {
      worldId: wid,
      worldTitle: world.title,
      worldIcon: world.icon,
      reason: 'explore',
    };
  }
  return null;
}

/**
 * Live-updating recommendation bundle. Re-derives on progress + age band change.
 * @returns {{ resume: Recommendation | null, todaysPick: Recommendation | null, exploreNext: ReturnType<typeof getExploreNextWorld> }}
 */
export function useRecommendations() {
  const progress = useProgressSnapshot();
  const reviewQueue = useReviewQueue();
  const [activeAgeBand] = useAgeBand();
  return useMemo(
    () => ({
      resume: getResumeTarget(progress),
      todaysPick: getTodaysPick(progress, reviewQueue, activeAgeBand),
      exploreNext: getExploreNextWorld(progress),
    }),
    [progress, reviewQueue, activeAgeBand],
  );
}

export const REASON_LABEL = {
  resume: 'Pick up where you left off',
  review: 'Time for a quick review',
  'continue-path': 'Continue your path',
  'fresh-age-match': 'A good match for you',
  'fresh-any': 'Try something new',
  explore: 'A whole new world',
};
